import { db } from "./firebase";
import { doc, getDoc, collection, getDocs, writeBatch, serverTimestamp } from "firebase/firestore";
import { COLLECTIONS } from "./collections";
import XpService, { XP_ACTION_TYPES } from "./xp_service";

/**
 * Gamification Engine Service
 * Responsável por automatizar a checagem e entrega de recompensas (achievements e níveis)
 * com base nas ações do usuário.
 */
const GamificationEngineService = {

    /**
     * Avalia as presenças e o XP do aluno para verificar se ele desbloqueou novas conquistas
     * ou subiu de nível.
     * DEVE ser chamado DEPOIS que a transação (ex: salvar presença) já houver sido concluída.
     * 
     * @param {string} idTenant
     * @param {string} idMember
     * @param {object} gamificationSettings - Configurações de gamificação do tenant
     * @param {object} user - Usuário que disparou a ação
     * @param {object} [existingMemberData] - Dados do membro já carregados (evita read extra). 
     *                                         Se omitido, busca do Firestore.
     */
    evaluateMemberGamification: async (idTenant, idMember, gamificationSettings, user, existingMemberData = null) => {
        if (!idTenant || !idMember || !gamificationSettings) return;

        try {
            // 1. Usa dados do membro se já fornecidos pelo caller, senão busca do Firestore
            const memberRef = doc(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember);
            let memberData = existingMemberData;

            if (!memberData) {
                const memberSnapshot = await getDoc(memberRef);
                if (!memberSnapshot.exists()) return;
                memberData = memberSnapshot.data();
            }
            
            const presentCount = memberData?.attendanceStats?.presentCount || 0;
            let finalXpTotal = memberData?.xpTotal || 0;

            // 2. Busca conquistas configuradas vs já ganhadas
            const awardedRef = collection(db, "tenants", idTenant, COLLECTIONS.MEMBERS, idMember, COLLECTIONS.AWARDED_ACHIEVEMENTS);
            const awardedSnapshot = await getDocs(awardedRef);
            const alreadyAwardedIds = awardedSnapshot.docs.map(d => d.id);

            const achievementsToAward = [];
            
            // 2.1 Verifica métricas de presença vs metas
            const attendanceAchievements = (gamificationSettings.achievements || []).filter(a => a.type === 'attendance');
            for (const ach of attendanceAchievements) {
                if (presentCount >= ach.threshold && !alreadyAwardedIds.includes(ach.id)) {
                    achievementsToAward.push(ach);
                }
            }

            // 2.2 Verifica metas de Pontos (XP) gerais
            // Exemplo: type === 'points' ou similar onde o threshold é o XP total
            const pointsAchievements = (gamificationSettings.achievements || []).filter(a => a.type !== 'attendance');
            for (const ach of pointsAchievements) {
                if (finalXpTotal >= (ach.threshold || 0) && !alreadyAwardedIds.includes(ach.id)) {
                    achievementsToAward.push(ach);
                }
            }

            const batch = writeBatch(db);
            let hasChangesToCommit = false;
            let extraXpToGive = 0;

            // 3. Empacota todas as novas conquistas em uma transação atômica
            if (achievementsToAward.length > 0) {
                achievementsToAward.forEach(ach => {
                    const newAchDoc = doc(awardedRef, ach.id); // Usamos o ID da conquista para blindar de duplicação
                    batch.set(newAchDoc, {
                        idAchievement: ach.id,
                        name: ach.name,
                        description: ach.description,
                        type: ach.type,
                        icon: ach.icon || null,
                        imageId: ach.imageId || null,
                        rewardXp: ach.rewardXp || 0,
                        threshold: ach.threshold || 0,
                        awardedAt: serverTimestamp(),
                    });

                    // Soma os XP de conquista e joga pro serviço injetar no batch
                    if (ach.rewardXp > 0) {
                        extraXpToGive += ach.rewardXp;
                        XpService.addXpInBatch(batch, idTenant, {
                            idMember,
                            xpAmount: ach.rewardXp,
                            actionType: XP_ACTION_TYPES.ACHIEVEMENT_REWARD,
                            sourceId: ach.id,
                            sourceName: `Conquista: ${ach.name}`,
                            user
                        });
                    }
                });

                hasChangesToCommit = true;
            }

            // 4. Avalia atualização de Patente / Nível do Membro (Rank)
            finalXpTotal += extraXpToGive;

            const levelRanks = gamificationSettings.levelRanks || [];
            // Ordenamos do maior rank para o menor: tenta encaixar no mais alto possível.
            const sortedRanks = [...levelRanks].sort((a, b) => b.minXp - a.minXp);
            const targetRank = sortedRanks.find(r => finalXpTotal >= r.minXp);

            const updates = {};
            if (targetRank) {
                const currentRankId = memberData?.currentRankId;
                if (currentRankId !== targetRank.id) {
                    updates.currentRankId = targetRank.id;
                    updates.currentRankName = targetRank.name;
                    updates.currentRankImageId = targetRank.imageId || targetRank.id;
                }
            }

            // Se for preciso atualizar o nível, anexa no batch
            if (Object.keys(updates).length > 0) {
                batch.update(memberRef, updates);
                hasChangesToCommit = true;
            }

            // Termina a transação se algo de novo foi acionado (conquistas ou novo nível)
            if (hasChangesToCommit) {
                await batch.commit();
            }

        } catch (error) {
            console.error("Erro na Gamification Engine:", error);
        }
    }
};

export default GamificationEngineService;
